/**
 * ComboDisplay.jsx
 * 
 * Phase 2: Combo text display system
 * Shows big centered text for multi-hit combos (3+ hits)
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';

// Combo adjectives for variety
const comboAdjectives = {
  3: ['Nice!', 'Good!', 'Solid!'],
  4: ['Great!', 'Sweet!', 'Clean!'],
  5: ['Awesome!', 'Sick!', 'Nasty!'],
  6: ['Amazing!', 'Incredible!', 'Brutal!'],
  7: ['Devastating!', 'Vicious!', 'Merciless!'],
  8: ['Unstoppable!', 'Legendary!', 'Godlike!'],
};

const getComboText = (hits) => {
  if (hits >= 8) {
    const options = comboAdjectives[8];
    return options[Math.floor(Math.random() * options.length)];
  }
  if (hits >= 7) {
    const options = comboAdjectives[7];
    return options[Math.floor(Math.random() * options.length)];
  }
  if (hits >= 6) {
    const options = comboAdjectives[6];
    return options[Math.floor(Math.random() * options.length)];
  }
  if (hits >= 5) {
    const options = comboAdjectives[5];
    return options[Math.floor(Math.random() * options.length)];
  }
  if (hits >= 4) {
    const options = comboAdjectives[4];
    return options[Math.floor(Math.random() * options.length)];
  }
  if (hits >= 3) {
    const options = comboAdjectives[3];
    return options[Math.floor(Math.random() * options.length)];
  }
  return null;
};

const getComboColor = (hits) => {
  if (hits >= 8) return '#ef4444'; // Red
  if (hits >= 6) return '#f59e0b'; // Orange
  if (hits >= 4) return '#eab308'; // Yellow
  return '#ffffff'; // White
};

export const ComboDisplay = React.forwardRef(({
  width = 1280,
  height = 720,
}, ref) => {
  const [combo, setCombo] = useState(null);
  const timeoutRef = useRef(null);
  const animationRef = useRef(null);

  // Expose methods to parent
  React.useImperativeHandle(ref, () => ({
    /**
     * Show a combo
     * @param {number} hits - Number of hits in the combo
     * @param {boolean} isLocalPlayer - Whether the local player dealt the combo
     */
    showCombo: (hits, isLocalPlayer = true) => {
      if (hits < 3) return; // Only show for 3+ hit combos

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const text = getComboText(hits);
      const color = getComboColor(hits);

      setCombo({
        hits,
        text,
        color,
        isLocalPlayer,
        id: Date.now(), // Force re-render for animation
      });

      // Auto-hide after 1.5 seconds
      timeoutRef.current = setTimeout(() => {
        setCombo(null);
      }, 1500);
    },

    /**
     * Clear any active combo display
     */
    clear: () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setCombo(null);
    },
  }));

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!combo) return null;

  return (
    <div
      className="combo-display"
      key={combo.id}
      style={{
        position: 'absolute',
        left: '50%',
        top: '40%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
        pointerEvents: 'none',
        zIndex: 100,
        animation: 'combo-pop-in 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
      }}
    >
      {/* Combo count */}
      <div
        style={{
          fontSize: '72px',
          fontWeight: '900',
          color: combo.color,
          textShadow: `
            0 0 20px ${combo.color}80,
            0 0 40px ${combo.color}40,
            0 4px 8px rgba(0,0,0,0.8)
          `,
          lineHeight: 1,
          marginBottom: '8px',
        }}
      >
        {combo.hits}
      </div>
      
      {/* Combo text */}
      <div
        style={{
          fontSize: '36px',
          fontWeight: '700',
          color: '#ffffff',
          textShadow: '0 2px 8px rgba(0,0,0,0.8)',
          letterSpacing: '2px',
          textTransform: 'uppercase',
        }}
      >
        {combo.text}
      </div>

      {/* Hit label */}
      <div
        style={{
          fontSize: '18px',
          fontWeight: '600',
          color: 'rgba(255,255,255,0.7)',
          textTransform: 'uppercase',
          letterSpacing: '4px',
          marginTop: '4px',
        }}
      >
        Hit{combo.hits > 1 ? 's' : ''}!
      </div>
    </div>
  );
});

ComboDisplay.displayName = 'ComboDisplay';

// Add keyframe animation to document if not present
if (typeof document !== 'undefined') {
  const styleId = 'combo-display-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes combo-pop-in {
        0% {
          opacity: 0;
          transform: translate(-50%, -50%) scale(0.5);
        }
        50% {
          transform: translate(-50%, -50%) scale(1.1);
        }
        100% {
          opacity: 1;
          transform: translate(-50%, -50%) scale(1);
        }
      }
      
      @keyframes combo-fade-out {
        0% {
          opacity: 1;
          transform: translate(-50%, -50%) scale(1);
        }
        100% {
          opacity: 0;
          transform: translate(-50%, -50%) scale(0.9) translateY(-20px);
        }
      }
      
      .combo-display {
        animation: combo-pop-in 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards,
                   combo-fade-out 0.3s ease-out 1.2s forwards;
      }
    `;
    document.head.appendChild(style);
  }
}

export default ComboDisplay;
